import { getJson, postJson } from './api';

import type { StreamEffectStyle } from '@ffs-studio/protocol';
export type { StreamEffectStyle } from '@ffs-studio/protocol';

export type EffectsSnapshot = {
  selectedStyle: StreamEffectStyle;
  activeSession: {
    id: string;
    style: StreamEffectStyle;
    welcomeGeneration: number;
    introSceneName: string | null;
    introSceneSeen: boolean;
    introSceneSeenAt: string | null;
    lastObservedSceneName: string | null;
    welcomeReleaseDueAt: string | null;
    startedAt: string;
    showStarted: boolean;
    showStartedAt: string | null;
    endedAt: string | null;
    welcomes: {
      queued: number;
      playing: number;
      welcomed: number;
      failed: number;
      skipped: number;
      total: number;
    };
  } | null;
  styles: Array<{
    style: StreamEffectStyle;
    label: string;
    startupSceneName: string | null;
    mainSceneName: string | null;
    welcomeReleaseDelayMs: number;
    welcomeReleaseRule: 'leave-startup' | 'enter-main';
    welcomeDurationMs: number;
    y2k: {
      enabled: boolean;
      intervalSeconds: number;
      durationSeconds: number;
    };
  }>;
  welcomeFallbacks: Array<{
    style: StreamEffectStyle;
    soundId: string | null;
    soundName: string | null;
    configured: boolean;
    enabled: boolean;
  }>;
};

export type ProductionSnapshot = {
  activeAgentId: string | null;
  activeAgent: {
    id: string;
    name: string;
    connected: boolean;
    agentVersion: string | null;
    capabilities: string[];
  } | null;
  obs: {
    status: 'online' | 'connecting' | 'offline' | 'disabled';
    currentProgramSceneName: string | null;
    scenes: string[];
    streaming: boolean;
    recording: boolean;
    obsVersion: string | null;
    obsWebSocketVersion: string | null;
    lastError: string | null;
    updatedAt: string;
  };
  soundboard: {
    status: 'online' | 'starting' | 'offline' | 'disabled';
    localUrl: string | null;
    overlayClients: number;
    currentPlaybackId: string | null;
    obsProvisioned: boolean;
    lastError: string | null;
    updatedAt: string;
  };
  textOutputs: {
    status: 'online' | 'starting' | 'offline' | 'disabled';
    outputDirectory: string | null;
    writtenFiles: number;
    lastWriteAt: string | null;
    nowPlaying: string | null;
    lastError: string | null;
    updatedAt: string;
  };
};

export type TwitchBotState = {
  configured: boolean;
  enabled: boolean;
  connected: boolean;
  scopesReady: boolean;
  botName?: string | null;
  lastError?: string | null;
};

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(payload.message ?? `${path} returned HTTP ${response.status}.`);
  }

  return (await response.json().catch(() => ({}))) as T;
}

export type StartStreamSound = {
  id: string;
  name: string;
  enabled: boolean;
};

export async function loadStartStreamState(): Promise<{
  effects: EffectsSnapshot;
  production: ProductionSnapshot;
  bot: TwitchBotState;
  sounds: StartStreamSound[];
}> {
  const [effects, production, bot, soundboard] = await Promise.all([
    getJson<EffectsSnapshot>('/api/v1/effects'),
    getJson<ProductionSnapshot>('/api/v1/production'),
    getJson<TwitchBotState>('/api/v1/twitch-bot'),
    getJson<{ sounds: StartStreamSound[] }>('/api/v1/soundboard'),
  ]);
  return { effects, production, bot, sounds: soundboard.sounds };
}

export function setStreamStyle(style: StreamEffectStyle): Promise<EffectsSnapshot> {
  return postJson<EffectsSnapshot>('/api/v1/effects/style', { style });
}

export function setWelcomeFallback(
  style: StreamEffectStyle,
  soundId: string | null,
): Promise<EffectsSnapshot> {
  return requestJson<EffectsSnapshot>('/api/v1/effects/welcome-fallback', {
    method: 'PUT',
    body: JSON.stringify({ style, soundId }),
  });
}

export function startStreamSession(): Promise<EffectsSnapshot> {
  return postJson<EffectsSnapshot>('/api/v1/effects/session/start');
}

export function releaseWelcomeQueue(): Promise<EffectsSnapshot> {
  return postJson<EffectsSnapshot>('/api/v1/effects/show/start');
}

export function startWelcomeCycle(): Promise<EffectsSnapshot> {
  return postJson<EffectsSnapshot>('/api/v1/effects/welcome-cycle/start');
}

export function endStreamSession(): Promise<EffectsSnapshot> {
  return postJson<EffectsSnapshot>('/api/v1/effects/session/end');
}

export function loadProduction(): Promise<ProductionSnapshot> {
  return getJson<ProductionSnapshot>('/api/v1/production');
}

export function switchObsScene(sceneName: string): Promise<{ ok: boolean; sceneName: string }> {
  return postJson('/api/v1/obs/scenes/switch', { sceneName });
}

export function refreshObsAudioSource(): Promise<Record<string, unknown>> {
  return postJson('/api/v1/soundboard/overlay/refresh');
}

export function stopObsStream(): Promise<{ ok: boolean; alreadyStopped?: boolean }> {
  return requestJson('/api/v1/obs/stream/stop', {
    method: 'POST',
    body: JSON.stringify({ confirmation: 'END STREAM' }),
  });
}
